// src/hooks/useXeroData.ts
// ─────────────────────────────────────────────────────────────────────────────
// Shared S2 Xero data hook — fetches /api/xero/status + /api/xero/data on
// mount, returns a normalised summary with gross/net margin pct derived.
// Requires the active client's xeroTenantId; short-circuits to a "not
// connected" empty state if none is bound.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from 'react';

export interface XeroSummary {
  revenue: number;
  costOfSales: number;
  grossProfit: number;
  grossMarginPct: number;
  staffCosts: number;
  ownerSalary: number;
  ownerPension: number;
  premises: number;
  marketing: number;
  professionalFees: number;
  itTechnology: number;
  vehicles: number;
  otherOperating: number;
  totalOperating: number;
  ebitda: number;
  financeAndDepreciation: number;
  netProfit: number;
  netMarginPct: number;
}

export interface XeroDataResult {
  loading: boolean;
  mapped: boolean;
  unmappedCount: number;
  summary: XeroSummary | null;
  error: string | null;
  tenantName: string | null;
}

export function useXeroData(tenantId: string | undefined): XeroDataResult {
  const [result, setResult] = useState<XeroDataResult>({
    loading: true,
    mapped: false,
    unmappedCount: 0,
    summary: null,
    error: null,
    tenantName: null,
  });

  useEffect(() => {
    if (!tenantId) {
      setResult({
        loading: false,
        mapped: false,
        unmappedCount: 0,
        summary: null,
        error: 'No Xero tenant bound to this client',
        tenantName: null,
      });
      return;
    }
    const q = `?tenantId=${encodeURIComponent(tenantId)}`;
    Promise.all([
      fetch(`/api/xero/status${q}`).then((r) => r.json()),
      fetch(`/api/xero/data${q}`).then((r) => r.json()),
    ])
      .then(([status, data]) => {
        if (!status.connected) {
          setResult((prev) => ({
            ...prev,
            loading: false,
            error: 'Xero not connected',
          }));
          return;
        }

        const ffm = data.ffm;
        if (!ffm?.mapped) {
          setResult((prev) => ({
            ...prev,
            loading: false,
            mapped: false,
            unmappedCount: ffm?.unmappedCount || 0,
            tenantName: status.tenantName,
          }));
          return;
        }

        const s = ffm.summary;
        const summary: XeroSummary = {
          ...s,
          grossMarginPct: s.revenue > 0
            ? Math.round((s.grossProfit / s.revenue) * 100)
            : 0,
          netMarginPct: s.revenue > 0
            ? Math.round((s.netProfit / s.revenue) * 100)
            : 0,
        };

        setResult({
          loading: false,
          mapped: true,
          unmappedCount: ffm.unmappedCount || 0,
          summary,
          error: null,
          tenantName: status.tenantName,
        });
      })
      .catch((err) => {
        setResult((prev) => ({
          ...prev,
          loading: false,
          error: err.message,
        }));
      });
  }, [tenantId]);

  return result;
}
